define((require) => {
    "use strict";

    const Backbone               = require("backbone");
    const ImageLibrarySourceEnum = require("shared/data/images/ImageLibrarySourceEnum");
    const ImageTagCollection     = require("ui/assets/image/tags/ImageTagCollection");
    const ImprovedModel          = require("shared/zbone/ImprovedModel");
    const LoggedIn               = require("shared/security/LoggedIn");
    const LoggedInUserState      = require("shared/ui/user/LoggedInUserState");
    const RestApi                = require("shared/services/RestApi");
    const Size                   = require("shared/util/Size");
    const UrlUtil                = require("shared/util/UrlUtil");
    const ImageTagModel          = require("ui/assets/image/tags/ImageTagModel");

    const sourceResourceMap = new Map();
    sourceResourceMap.set(ImageLibrarySourceEnum.CREATOR, RestApi.Collections.IMAGES);
    sourceResourceMap.set(ImageLibrarySourceEnum.SCENE7, RestApi.Collections.SCENE7_IMAGES);

    /**
     * Editor Image Model representing editable metadata about an image asset.
     *
     * @class ImageModel
     */
    return ImprovedModel.extend("Image", {

        defaults() {
            const principal = LoggedIn.principal();

            return {
                altText:        "",
                height:         0,
                name:           "Untitled",
                supportsResize: false,
                thumbUrl:       "",
                url:            "",
                width:          0,
                version:        null,
                size:           0,
                timesUsed:      0,
                createdAt:      0,
                createdBy:      principal.get("userId"),
                companyId:      principal.get("companyId"),
                tags:           [] // ImageTagCollection
            };
        },

        relations: [{
            type: Backbone.Many,
            key: "tags",
            collectionType: ImageTagCollection
        }],

        computed: {
            absoluteThumbUrl: {
                deps: ["thumbUrl", "url"],
                value(thumbUrl, url) {
                    return this._getAbsoluteUrl(thumbUrl || url);
                }
            },

            /**
             * @type {String}
             * The file extension of the image, e.g. <tt>png</tt> for an image with an url like <tt>bar/foo.png</tt>.
             */
            extension: {
                deps: ["url", "name"], // default to extension from name on upload, until actual model data is ready
                value(url, name) {
                    const ext = (nameOrUrl) => {
                        // Can still have a name with no extension...
                        if (nameOrUrl) {
                            const i = nameOrUrl.lastIndexOf(".");
                            if (i !== -1) {
                                return nameOrUrl.substr(i + 1).toLowerCase(); // can still be empty
                            }
                        }
                        return false;
                    };
                    return ext(url) || ext(name) || "?"; // e.g. ? on upload of legal image with no extension in file name
                }
            },

            /**
             * Returns the number of bytes in a human readable format.
             *
             * @return {String} The number of bytes in human-readable format.
             *
             * @type {String}
             */
            displaySize: {
                deps: ["size"],
                value(size) {
                    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
                    let posttxt = 0;

                    if (size === 0) {
                        return "";
                    }

                    while (size >= 1000) {
                        posttxt++;
                        size /= 1024;
                    }

                    return parseInt(size, 10).toFixed(0) + " " + sizes[posttxt];
                }
            },

            imageTags: {
                deps: ["tags"],
                value(tags) {
                    const imageTags = tags.filter((imageTag) => {
                        return imageTag.get("type") !== ImageTagModel.Types.ZMAGS_GROUP_ID;
                    });

                    var imageTagCollection = new ImageTagCollection(imageTags);

                    return imageTagCollection;
                }
            },
            inventoryTags: {
                deps: ["tags"],
                value(tags) {
                    const inventoryTags = tags.filter((inventoryTag) => {
                        return inventoryTag.get("type") === ImageTagModel.Types.ZMAGS_GROUP_ID;
                    });
                    var inventoryTagCollection = new ImageTagCollection(inventoryTags);

                    return inventoryTagCollection;
                }
            }
        },

        transient: [
            // Used for showing progress when uploading
            "uploading",
            "progress",
            "timesUsed" // Computed by the backend
        ],

        /**
         * @returns {Size}
         */
        getSize() {
            return new Size(this.get("width"), this.get("height"));
        },

        _distinguishedName() {
            return this.get("name");
        },

        _getAbsoluteUrl(url) {
            const sourceType = this.get("sourceType");
            if (sourceType) {
                const servingUrl = LoggedInUserState.get("integrations").getImageSourceTypeServingUrl(sourceType);
                return UrlUtil.concat(servingUrl, url);
            }

            return url && UrlUtil.getAbsoluteViewerApiUrl(url);
        },

        /**
         * Utility function to get the source which this image belongs based on the <tt>sourceType</tt> attribute.
         * The lookup happens in the <tt>ImageLibrarySourceEnum</tt> which contains all valid source types.
         *
         * @return {ImageLibrarySourceEnum}  A <tt>ImageLibrarySourceEnum</tt> constant if this model has a source defined otherwise undefined.
         */
        getSource() {
            const sourceType = this.get("sourceType");
            const source = sourceType && ImageLibrarySourceEnum.lookup(sourceType, true);
            return source;
        },

        /**
         * Utility function to check if the model source is equal to another.
         *
         * Example:
         * <pre>
         *    const isScene7 = model.isSource(ImageLibrarySourceEnum.SCENE7)
         * </pre>
         *
         * @param  {ImageLibrarySourceEnum}  compareSource A ImageLibrarySourceEnum constant
         * @return {Boolean} true if the provided source is equal to the one in this model
         */
        isSource(compareSource) {
            const source = this.getSource();
            return source && source === compareSource;
        },

        url() {
            const source = this.getSource() || ImageLibrarySourceEnum.CREATOR;
            const resource = sourceResourceMap.get(source);

            if (this.isNew()) {
                return RestApi.pathToCollection(resource);
            }

            return RestApi.pathToSpecificResource(resource, this.get("id"));
        }

    }, {
        findByIdFromSource(id, sourceType) {
            return this.findByQuery({id, sourceType});
        },

        resourceFromSource(source) {
            const lookupSource = ImageLibrarySourceEnum.lookup(source);
            return sourceResourceMap.get(lookupSource);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/assets/image/ImageModel.js